import { Link } from "@mui/material";

import {
	FacebookLogo,
	TiktokLogo,
	InstagramLogo,
	SnapchatLogo,
	TwitterLogo,
	YoutubeLogo,
	GithubLogo,
	PinterestLogo,
} from "phosphor-react";
import { ReactNode } from "react";

import { ensureHttpProtocol } from "@/utils/url";

import { TableColumn } from "@/components/Table/Table";

import yelp from "@/assets/icons/yelp.png";

import { ListPerson } from "../../schema/list";
import { MapBusiness } from "../../../Map/schema/mapBusiness";
import { DEFAULT_COLUMN_OPTIONS_HIDE } from "./columnFactory";

type SocialMediaUrlField = keyof Pick<
	MapBusiness,
	| "pinterestUrl"
	| "githubUrl"
	| "youtubeUrl"
	| "twitterUrl"
	| "snapchatUrl"
	| "instagramUrl"
	| "tiktokUrl"
	| "yelpUrl"
	| "facebookUrl"
>;

interface SocialMediaConfig {
	key: string;
	urlField: SocialMediaUrlField;
	icon: ReactNode;
}

/**
 * Factory function to create a social media column for Google Maps data
 */
const createSocialMediaColumn = (
	ts: (key: string) => string,
	config: SocialMediaConfig
): TableColumn => ({
	...DEFAULT_COLUMN_OPTIONS_HIDE,
	title: ts(config.key),
	uid: config.key,
	render: (row: ListPerson) => (
		<div className="flex">
			{row.mapBusiness?.[config.urlField] && (
				<Link href={ensureHttpProtocol(row.mapBusiness[config.urlField] as string)} target="_blank">
					{config.icon}
				</Link>
			)}
		</div>
	),
});

// Social media configurations
const socialMediaConfigs: SocialMediaConfig[] = [
	{ key: "pinterest", urlField: "pinterestUrl", icon: <PinterestLogo size="20" /> },
	{ key: "github", urlField: "githubUrl", icon: <GithubLogo size="20" /> },
	{ key: "youtube", urlField: "youtubeUrl", icon: <YoutubeLogo size="20" /> },
	{ key: "twitter", urlField: "twitterUrl", icon: <TwitterLogo size="20" /> },
	{ key: "snapchat", urlField: "snapchatUrl", icon: <SnapchatLogo size="20" /> },
	{ key: "instagram", urlField: "instagramUrl", icon: <InstagramLogo size="20" /> },
	{ key: "tiktok", urlField: "tiktokUrl", icon: <TiktokLogo size="20" /> },
	{ key: "yelp", urlField: "yelpUrl", icon: <img alt="yelp" className="h-[20px]" src={yelp} /> },
	{ key: "facebook", urlField: "facebookUrl", icon: <FacebookLogo size="20" /> },
];

// Export individual column functions for backwards compatibility
export const renderGoogleMapsPinterestColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[0]);

export const renderGoogleMapsGithubColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[1]);

export const renderGoogleMapsYoutubeColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[2]);

export const renderGoogleMapsTwitterColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[3]);

export const renderGoogleMapsSnapchatColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[4]);

export const renderGoogleMapsInstagramColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[5]);

export const renderGoogleMapsTiktokColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[6]);

export const renderGoogleMapsYelpColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[7]);

export const renderGoogleMapsFacebookColumn = (ts: (key: string) => string): TableColumn =>
	createSocialMediaColumn(ts, socialMediaConfigs[8]);
